import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { NativeSelect } from '#src/atoms/inputs/NativeSelect/index.tsx';
import { Actions as ActionsLayout } from '#src/atoms/layout/Actions/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  BodyS,
  HeadlineL,
  LabelM,
} from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';

import type {
  RequestJonumSelfExclusionMutation,
  RequestJonumSelfExclusionMutationVariables,
} from './__generated__/index.graphql.ts';

const REQUEST_JONUM_SELF_EXCLUSION = gql`
  mutation RequestJonumSelfExclusionMutation(
    $input: requestJonumSelfExclusionInput!
  ) {
    requestJonumSelfExclusion(input: $input) {
      verification {
        id
        selfExcludedUntil
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  RequestJonumSelfExclusionMutation,
  RequestJonumSelfExclusionMutationVariables
>;

const Content = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
`;

const Actions = styled(ActionsLayout).attrs({
  direction: 'vertical',
  gap: 1,
})`
  width: 100%;
  margin-top: var(--unit);
`;

const Footer = styled(BodyS)`
  color: var(--c-nd-600);
  text-align: center;
`;

const StyledSelect = styled(NativeSelect)`
  width: 100%;
  background-color: var(--c-black);
`;

const HOUR_MS = 60 * 60 * 1000;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const SelfExclusionDialog = ({ open, onClose }: Props) => {
  const { formatMessage, formatNumber } = useIntl();
  const [value, setValue] = useState('');

  const [requestSelfExclusion] = useMutation(REQUEST_JONUM_SELF_EXCLUSION, {
    showErrorsWithSnackNotification: true,
  });

  const options = useMemo(
    () =>
      (
        [
          { hours: 24, amount: 24, unit: 'hour' },
          { hours: 2 * 24, amount: 48, unit: 'hour' },
          { hours: 7 * 24, amount: 7, unit: 'day' },
          { hours: 30 * 24, amount: 30, unit: 'day' },
          { hours: 6 * 30 * 24, amount: 6, unit: 'month' },
          { hours: 365 * 24, amount: 12, unit: 'month' },
        ] as const
      ).map(({ hours, amount, unit }) => ({
        hours,
        label: formatNumber(amount, {
          style: 'unit',
          unit,
          unitDisplay: 'long',
        }),
      })),
    [formatNumber]
  );

  const handleClose = () => {
    setValue('');
    onClose();
  };

  const handleConfirm = async () => {
    if (value === '') return;
    const exclusionEndDate = new Date(Date.now() + Number(value) * HOUR_MS);
    const result = await requestSelfExclusion({
      variables: { input: { exclusionEndDate } },
    });
    if (!result.data?.requestJonumSelfExclusion?.errors?.length) {
      handleClose();
    }
  };

  const isValid = value !== '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      hideHeader
      maxWidth="xs"
      fullWidth
    >
      <Content>
        <HeadlineL color="var(--c-white)" bold>
          <FormattedMessage defaultMessage="Are you sure you want to self-exclude?" />
        </HeadlineL>
        <BodyM color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="By self-excluding, you will not be able to access Sorare for the duration you choose. This action is immediate and cannot be undone." />
        </BodyM>
        <LabelM bold color="var(--c-white)">
          <FormattedMessage defaultMessage="Duration*" />
        </LabelM>
        <StyledSelect
          values={options.map(({ hours, label }) => ({
            value: String(hours),
            label,
          }))}
          value={value}
          onChange={setValue}
          placeholder={formatMessage({ defaultMessage: 'Select a duration' })}
          fullWidth
        />
        <Actions>
          <Button
            size="medium"
            color="red"
            onClick={handleConfirm}
            disabled={!isValid}
            fullWidth
          >
            {isValid ? (
              <FormattedMessage defaultMessage="Self-exclude me" />
            ) : (
              <FormattedMessage defaultMessage="Confirm self-exclusion" />
            )}
          </Button>
          <Button
            size="medium"
            color="transparent"
            onClick={handleClose}
            fullWidth
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
        </Actions>
        <Footer>
          <FormattedMessage defaultMessage="During this period, you will not be able to create or get verified on another account." />
        </Footer>
      </Content>
    </Dialog>
  );
};
